import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, Check, Compass, Loader2, X } from 'lucide-react';
import { steeringService, steerError, SteeringKind, SteeringNote } from '../../services/steeringService';

const POLL_MS = 15_000;

type PickableKind = Exclude<SteeringKind, 'control'>;

const KIND_STYLE: Record<SteeringKind, { label: string; chip: string; hint: string }> = {
  correction: { label: 'Correction', chip: 'bg-rose-50 text-rose-700 border-rose-200', hint: 'Fix a name, term or fact the drain got wrong' },
  policy: { label: 'Policy', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200', hint: 'A standing rule for the rest of the session' },
  material: { label: 'Material', chip: 'bg-amber-50 text-amber-700 border-amber-200', hint: 'Pasted text read once by the next drain' },
  request: { label: 'Request', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', hint: 'Ask the platform to do something now' },
  control: { label: 'Control', chip: 'bg-slate-50 text-slate-600 border-slate-200', hint: '' },
  other: { label: 'Other', chip: 'bg-slate-50 text-slate-500 border-slate-200', hint: '' },
};
const PICKABLE: PickableKind[] = ['request', 'policy', 'correction', 'material'];
const EXAMPLES = ['Build a timeline slide of the chase steps', 'Comerford, not Copperford', 'Keep attendee names out of decisions'];

const SOURCE_LABEL: Record<string, string> = {
  web: 'Web', hud: 'HUD', ring: 'Ring', 'claude-code': 'Claude Code', mcp: 'MCP', system: 'Platform',
};

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

interface Props {
  sessionId: string;
  source: 'web' | 'hud';
  canEdit?: boolean;
  /** Reports the number of active notes, for a badge on a closed popover. */
  onCount?: (n: number) => void;
  autoFocus?: boolean;
}

/** Typed steering notes for the platform drain and goals (agentic core R1a, WS1). */
const SteerBox: React.FC<Props> = ({ sessionId, source, canEdit = true, onCount, autoFocus }) => {
  const [notes, setNotes] = useState<SteeringNote[]>([]);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<PickableKind | 'auto'>('auto');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [sentAs, setSentAs] = useState<SteeringKind | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;

  const load = useCallback(async () => {
    try {
      const all = await steeringService.list(sessionId, 'active');
      setNotes(all.filter((n) => n.kind !== 'control' && n.status === 'active'));
    } catch {
      /* keep the last list; the next poll retries */
    }
  }, [sessionId]);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    onCountRef.current?.(notes.length);
  }, [notes.length]);

  // Grow the box for pasted material, up to about six lines. A hidden popover measures 0: leave the CSS minimum then.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    if (el.scrollHeight > 0) el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [text, autoFocus]);

  useEffect(() => {
    if (!sentAs) return;
    const t = setTimeout(() => setSentAs(null), 4000);
    return () => clearTimeout(t);
  }, [sentAs]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    setWarnings([]);
    try {
      const res = await steeringService.steer(sessionId, { text: body, source, ...(kind !== 'auto' ? { kind } : {}) });
      setText('');
      setKind('auto');
      setWarnings(res.warnings ?? []);
      setSentAs(res.note.kind);
      if (res.note.status === 'active' && res.note.kind !== 'control') {
        setNotes((prev) => [...prev.filter((n) => n.id !== res.note.id), res.note]);
      }
    } catch (e: any) {
      setError(steerError(e, 'Could not send the note'));
    } finally {
      setSending(false);
    }
  };

  const withdraw = async (note: SteeringNote) => {
    setError(null);
    setNotes((prev) => prev.filter((n) => n.id !== note.id));
    try {
      await steeringService.update(sessionId, note.id, 'withdrawn');
    } catch (e: any) {
      setNotes((prev) => [...prev, note].sort((a, b) => a.seq - b.seq));
      setError(steerError(e, 'Could not withdraw the note'));
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const shown = [...notes].reverse();

  return (
    <div className="flex flex-col gap-3 text-left" data-testid="steer-box">
      {canEdit && (
        <div className="border border-[#e2e8f0] rounded-xl bg-white focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent transition-shadow">
          <div className="flex items-start gap-2 px-3 pt-2.5">
            <Compass size={16} className="text-primary shrink-0 mt-0.5" />
            <textarea
              ref={inputRef}
              rows={1}
              value={text}
              autoFocus={autoFocus}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={sending}
              placeholder="Tell the platform what to focus on, fix or make…"
              aria-label="Steering note"
              className="flex-1 min-h-[20px] resize-none border-0 p-0 bg-transparent text-sm leading-5 text-navy placeholder:text-slate-400 shadow-none outline-none focus:ring-0 focus:outline-none"
            />
          </div>
          <div className="flex items-center justify-between gap-2 px-2 pb-2 pt-2">
            <div role="radiogroup" aria-label="Kind" className="flex flex-wrap items-center gap-1">
              {(['auto', ...PICKABLE] as Array<PickableKind | 'auto'>).map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={kind === k}
                  onClick={() => setKind(k)}
                  title={k === 'auto' ? 'The platform decides what kind of note this is' : KIND_STYLE[k].hint}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${
                    kind === k ? 'bg-navy text-white border-navy' : 'bg-white text-slate-500 border-[#e2e8f0] hover:border-slate-300'
                  }`}
                >
                  {k === 'auto' ? 'Auto' : KIND_STYLE[k].label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={send}
              disabled={sending || !text.trim()}
              aria-label="Send note"
              title="Send (Enter). Shift+Enter for a new line."
              className="h-7 w-7 shrink-0 flex items-center justify-center rounded-lg bg-primary hover:bg-primary-dark text-white disabled:bg-slate-100 disabled:text-slate-400 transition-colors"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <ArrowUp size={14} />}
            </button>
          </div>
        </div>
      )}

      {sentAs && !error && (
        <p className="flex items-center gap-1 text-xs text-slate-500" role="status">
          <Check size={12} className="text-primary" /> Sent as {(KIND_STYLE[sentAs] ?? KIND_STYLE.request).label.toLowerCase()}.
        </p>
      )}
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
      {warnings.map((w) => (
        <p key={w} className="text-xs text-amber-600">{w}</p>
      ))}

      {shown.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] font-bold text-slate-400 tracking-widest uppercase">In force ({shown.length})</p>
          <ul className="flex flex-col divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-xl bg-white max-h-64 overflow-y-auto" aria-label="Active steering notes">
            {shown.map((n) => {
              const style = KIND_STYLE[n.kind] ?? KIND_STYLE.request;
              return (
                <li key={n.id} className="group flex items-start gap-2 px-3 py-2">
                  <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${style.chip}`}>{style.label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-700 line-clamp-2 whitespace-pre-line" title={n.text}>{n.text}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{SOURCE_LABEL[n.source] ?? n.source} · {time(n.createdAt)}</p>
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => withdraw(n)}
                      aria-label={`Withdraw: ${n.text}`}
                      title="Withdraw"
                      className="shrink-0 p-0.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        canEdit && (
          <div className="text-xs text-slate-400">
            <p className="mb-1">Nothing in force yet. For example:</p>
            <ul className="flex flex-col gap-0.5">
              {EXAMPLES.map((e) => (
                <li key={e}>
                  <button type="button" onClick={() => { setText(e); inputRef.current?.focus(); }} className="text-left text-slate-500 hover:text-primary transition-colors">
                    “{e}”
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )
      )}
    </div>
  );
};

/** Header button that opens the steer box in a popover; the box stays mounted so the badge stays current. */
export const SteerButton: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center justify-center gap-2 rounded-lg h-9 px-3 text-white border text-sm font-medium transition-colors ${
          open ? 'bg-primary/20 border-primary/40' : 'bg-white/5 hover:bg-white/10 border-white/10'
        }`}
        title="Steer the platform"
      >
        <Compass size={14} />
        <span className="hidden md:inline">Steer</span>
        {count > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-navy text-[10px] font-bold flex items-center justify-center">{count}</span>}
      </button>
      <div className={`absolute right-0 top-11 z-30 w-[26rem] bg-white border border-[#e2e8f0] rounded-xl shadow-lg p-4 whitespace-normal ${open ? '' : 'hidden'}`}>
        <p className="text-sm font-bold text-navy">Steer the platform</p>
        <p className="text-xs text-slate-500 mb-3">Guides the live record and the session's agents until you withdraw it.</p>
        <SteerBox sessionId={sessionId} source="web" onCount={setCount} autoFocus={open} />
      </div>
    </div>
  );
};

export default SteerBox;
